import React, { useEffect, useState, useRef } from "react";

const LARAVEL_API_URL = "https://api.altasalud.com.ar/api/web/googlereviews";

const GoogleReviewsDirect = () => {
  const [reviews, setReviews] = useState([]);
  const [rating, setRating] = useState(4.5); 
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedReviews, setExpandedReviews] = useState({});

  // Refs y Estados para el efecto de arrastrar (Drag-to-Scroll)
  const containerRef = useRef(null);
  const [isDown, setIsDown] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await fetch(LARAVEL_API_URL, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json"
          },
        });
  
        if (!response.ok) {
          const errorJson = await response.json().catch(() => ({}));
          throw new Error(errorJson.message || `Error del servidor: ${response.status}`);
        }
  
        const data = await response.json();
  
        // Mapeo flexible para soportar ambas estructuras de la API globales
        setRating(data.rating || data.rating_global || 4.5);
        setTotalCount(data.userRatingCount || data.total_global || 0); 
        setReviews(data.reviews || []);
        setLoading(false);
      } catch (err) {
        console.error("Error al traer reseñas desde Laravel:", err);
        setError(err.message);
        setLoading(false);
      }
    };
  
    fetchReviews();
  }, []);

  // Lógica de navegación por flechas (PC)
  const scroll = (direction) => {
    if (containerRef.current) {
      const cardWidth = 320;
      const gap = 24; // gap-4 equivale a 24px en Bootstrap
      const scrollAmount = cardWidth + gap;
      
      containerRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth"
      });
    }
  };
  // Lógica del Slider Manual (Mouse Drag)
  const handleMouseDown = (e) => {
    setIsDown(true);
    setStartX(e.pageX - containerRef.current.offsetLeft);
    setScrollLeft(containerRef.current.scrollLeft);
  };

  const handleMouseLeave = () => {
    setIsDown(false);
  };

  const handleMouseUp = () => {
    setIsDown(false);
  };

  const handleMouseMove = (e) => {
    if (!isDown) return;
    e.preventDefault();
    const x = e.pageX - containerRef.current.offsetLeft;
    const walk = (x - startX) * 1.8; // Multiplicador de velocidad de arrastre
    containerRef.current.scrollLeft = scrollLeft - walk;
  };

  const toggleExpand = (index, e) => {
    e.stopPropagation(); // Evita conflictos con el arrastre del ratón
    setExpandedReviews((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  if (loading) return <div className="text-center my-5 text-secondary py-4">Cargando comentarios de Google...</div>;
  if (error) return <div className="text-center my-5 text-danger py-4">No se pudieron cargar las opiniones de Google.</div>;
  if (reviews.length === 0) return null;

  return (
    <div className="container my-5 py-4" style={{ fontFamily: "'Inter', sans-serif", maxWidth: "1140px" }}>
      
      <h2 className="text-center mb-4 fw-bold" style={{ fontSize: "28px", letterSpacing: "-0.5px", color: "#212529" }}>
        Lo que dicen nuestros pacientes
      </h2>
      <hr class="lines_after-titles"></hr>
      {/* Encabezado Estilo Elfsight Premium */}
      <div 
        className="p-4 mb-5 border-0" 
        style={{ 
          backgroundColor: "#f6f6f8", 
          borderRadius: "16px",
          boxShadow: "0 4px 24px rgba(0, 0, 0, 0.06)",
          fontFamily: "inherit",
          marginTop: "20px",
        }}
      >
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-4">
          
          {/* Bloque Izquierdo: Info de Google, Estrellas y Score */}
          <div className="d-flex flex-column flex-sm-row align-items-sm-center gap-3 text-start">
            
            {/* Logo de Google Integrado */}
            <div className="d-flex align-items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 85 36" style={{ width: "75px", height: "auto" }}>
                <g>
                  <path fill="#4285F4" d="M20.778 13.43h-9.862v2.927h6.994c-.345 4.104-3.76 5.854-6.982 5.854-4.123 0-7.72-3.244-7.72-7.791 0-4.43 3.429-7.841 7.73-7.841 3.317 0 5.272 2.115 5.272 2.115l2.049-2.122s-2.63-2.928-7.427-2.928C4.725 3.644 0 8.8 0 14.367c0 5.457 4.445 10.777 10.988 10.777 5.756 0 9.969-3.942 9.969-9.772 0-1.23-.179-1.941-.179-1.941Z"></path>
                  <path fill="#EA4335" d="M28.857 11.312c-4.047 0-6.947 3.163-6.947 6.853 0 3.744 2.813 6.966 6.994 6.966 3.786 0 6.887-2.893 6.887-6.886 0-4.576-3.607-6.933-6.934-6.933Zm.04 2.714c1.99 0 3.876 1.609 3.876 4.201 0 2.538-1.878 4.192-3.885 4.192-2.205 0-3.945-1.766-3.945-4.212 0-2.394 1.718-4.181 3.954-4.181Z"></path>
                  <path fill="#FBBC05" d="M43.965 11.312c-4.046 0-6.946 3.163-6.946 6.853 0 3.744 2.813 6.966 6.994 6.966 3.785 0 6.886-2.893 6.886-6.886 0-4.576-3.607-6.933-6.934-6.933Zm.04 2.714c1.99 0 3.876 1.609 3.876 4.201 0 2.538-1.877 4.192-3.885 4.192-2.205 0-3.945-1.766-3.945-4.212 0-2.394 1.718-4.181 3.955-4.181Z"></path>
                  <path fill="#4285F4" d="M58.783 11.319c-3.714 0-6.634 3.253-6.634 6.904 0 4.16 3.385 6.918 6.57 6.918 1.97 0 3.017-.782 3.79-1.68v1.363c0 2.384-1.448 3.812-3.633 3.812-2.11 0-3.169-1.57-3.537-2.46l-2.656 1.11c.943 1.992 2.839 4.07 6.215 4.07 3.693 0 6.508-2.327 6.508-7.205V11.734h-2.897v1.17c-.89-.96-2.109-1.585-3.726-1.585Zm.269 2.709c1.821 0 3.69 1.554 3.69 4.21 0 2.699-1.865 4.187-3.73 4.187-1.98 0-3.823-1.608-3.823-4.161 0-2.653 1.914-4.236 3.863-4.236Z"></path>
                  <path fill="#EA4335" d="M78.288 11.302c-3.504 0-6.446 2.788-6.446 6.901 0 4.353 3.28 6.934 6.782 6.934 2.924 0 4.718-1.6 5.789-3.032l-2.389-1.59c-.62.962-1.656 1.902-3.385 1.902-1.943 0-2.836-1.063-3.39-2.094l9.266-3.845-.48-1.126c-.896-2.207-2.984-4.05-5.747-4.05Zm.12 2.658c1.263 0 2.171.671 2.557 1.476l-6.187 2.586c-.267-2.002 1.63-4.062 3.63-4.062Z"></path>
                  <path fill="#34A853" d="M67.425 24.727h3.044V4.359h-3.044v20.368Z"></path>
                </g>
              </svg>
              <span className="fw-semibold text-secondary fs-5 align-middle ms-1">Reseñas</span>
            </div>

            {/* Contenedor de Calificación Física Dinámica */}
            <div className="d-flex align-items-center gap-2 bg-light px-3 py-2 rounded-pill">
              <span className="fw-bold text-dark fs-5 mb-0" style={{ lineHeight: "1" }}>
                4.5
                {/* {typeof rating === 'number' ? rating.toFixed(1).replace('.', ',') : rating} */}
              </span>
              
              {/* Renderizado de Estrellas SVG del código Elfsight */}
              <div className="d-flex align-items-center gap-1">
                {[...Array(5)].map((_, index) => {
                  const isFilled = index < Math.round(rating);
                  return (
                    <svg 
                      key={index}
                      viewBox="0 0 14 14" 
                      style={{ width: "16px", height: "16px" }}
                    >
                      <path 
                        d="M6.82617 11.442L3.54617 13.166C3.46353 13.2093 3.3704 13.2287 3.27732 13.2219C3.18425 13.2151 3.09494 13.1824 3.0195 13.1274C2.94406 13.0725 2.8855 12.9975 2.85045 12.911C2.8154 12.8245 2.80526 12.7299 2.82117 12.638L3.44817 8.98798C3.46192 8.908 3.456 8.82587 3.43091 8.74869C3.40582 8.67151 3.36232 8.6016 3.30417 8.54499L0.650168 5.95899C0.583317 5.89388 0.53602 5.81136 0.51363 5.72076C0.491239 5.63017 0.494647 5.53512 0.52347 5.44637C0.552292 5.35761 0.605378 5.27869 0.676721 5.21854C0.748065 5.15838 0.834818 5.1194 0.927168 5.10599L4.59317 4.57299C4.67344 4.56146 4.7497 4.53059 4.81537 4.48303C4.88105 4.43547 4.93418 4.37265 4.97017 4.29999L6.61017 0.977985C6.65153 0.894518 6.7154 0.824266 6.79455 0.775151C6.87371 0.726037 6.96501 0.700012 7.05817 0.700012C7.15132 0.700012 7.24263 0.726037 7.32178 0.775151C7.40094 0.824266 7.4648 0.894518 7.50617 0.977985L9.14717 4.29899C9.18307 4.37152 9.23604 4.43426 9.30153 4.48182C9.36702 4.52937 9.44308 4.56031 9.52317 4.57199L13.1892 5.10499C13.2815 5.1184 13.3683 5.15738 13.4396 5.21754C13.511 5.27769 13.564 5.35661 13.5929 5.44537C13.6217 5.53412 13.6251 5.62917 13.6027 5.71976C13.5803 5.81036 13.533 5.89288 13.4662 5.95798L10.8132 8.54398C10.7552 8.60049 10.7118 8.67024 10.6867 8.74723C10.6616 8.82422 10.6556 8.90616 10.6692 8.98598L11.2962 12.637C11.3122 12.7291 11.3021 12.8238 11.267 12.9105C11.232 12.9971 11.1733 13.0722 11.0977 13.1272C11.0221 13.1822 10.9326 13.2149 10.8393 13.2215C10.7461 13.2282 10.6528 13.2086 10.5702 13.165L7.29117 11.441C7.21946 11.4033 7.13967 11.3836 7.05867 11.3836C6.97767 11.3836 6.89788 11.4033 6.82617 11.441V11.442Z" 
                        fill={isFilled ? "#FFB700" : "none"} 
                        stroke={isFilled ? "#FFB700" : "#D1D5DB"}
                        strokeWidth={isFilled ? "0" : "1"}
                      />
                    </svg>
                  );
                })}
              </div>

              {/* Volumen Real de Reseñas Dinámico */}
              <span className="text-muted small fw-medium mb-0" style={{ lineHeight: "1" }}>
                (144 opiniones)
              </span>
            </div>

          </div>

          {/* Bloque Derecho: Botón de Llamado a la Acción (CTA) */}
          <div className="text-start text-md-end">
            <a 
              href="https://search.google.com/local/writereview?placeid=ChIJOY6O-e3JvJURBL72m9AN41c" 
              target="_blank" 
              rel="noopener noreferrer"
              className="btn d-inline-flex align-items-center justify-content-center text-decoration-none transition-all"
              style={{
                backgroundColor: "#197bff",
                color: "#ffffff",
                borderRadius: "24px",
                fontWeight: "700",
                fontSize: "14px",
                padding: "10px 24px",
                border: "none",
                boxShadow: "0 4px 12px rgba(25, 123, 255, 0.25)",
                cursor: "pointer"
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#1464cc"}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "#197bff"}
            >
              Dejanos una reseña en Google
            </a>
          </div>

        </div>
      </div>

{/* Contenedor Posicionamiento Relativo para las Flechas */}
<div className="position-relative px-md-2">
        
        {/* Flecha Izquierda - Oculta en Mobile */}
        <button 
          onClick={() => scroll("left")}
          className="btn btn-light rounded-circle d-none d-md-flex align-items-center justify-content-center position-absolute slider-arrow-btn"
          style={{
            left: "-20px",
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 10,
            width: "44px",
            height: "44px",
            boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
            border: "1px solid #e2e8f0",
            backgroundColor: "#ffffff",
            transition: "all 0.2s ease"
          }}
          aria-label="Anterior"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="#4A5568" viewBox="0 0 16 16">
            <path fillRule="evenodd" d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0z"/>
          </svg>
        </button>
      {/* Contenedor Deslizable con Arrastre (Drag-to-Scroll) */}
      <div 
        ref={containerRef}
        onMouseDown={handleMouseDown}
        onMouseLeave={handleMouseLeave}
        onMouseUp={handleMouseUp}
        onMouseMove={handleMouseMove}
        className={`d-flex gap-4 overflow-auto pb-4 text-start hide-scrollbar ${
          reviews.length <= 3 ? "justify-content-center" : "justify-content-start"
        } ${isDown ? "is-grabbing" : "is-grab"}`}
        style={{
          scrollSnapType: isDown ? "none" : "x mandatory", 
          WebkitOverflowScrolling: "touch",
          userSelect: "none", 
        }}
      >
        {reviews.map((review, index) => {
          // Soporte robusto multi-estructura para las propiedades individuales de cada reseña
          const reviewText = (typeof review.text === 'object' ? review.text?.text : review.text) || review.snippet || "Sin comentario escrito.";
          const authorName = review.authorAttribution?.displayName || review.user?.name || "Paciente de Alta Salud";
          const authorPhoto = review.authorAttribution?.photoUri || review.user?.thumbnail || "https://via.placeholder.com/44";
          const publishTime = review.relativePublishTimeDescription || review.date || "Reciente";
          const currentRating = review.rating || 5;
          
          const isExpanded = !!expandedReviews[index];
          const isLongText = reviewText && reviewText.length > 130;

          return (
            <div 
              key={index} 
              className="review-card p-4 rounded-4 flex-shrink-0 d-flex flex-column justify-content-between" 
              style={{ 
                backgroundColor: "#ffffff", 
                width: "300px",
                height: isExpanded ? "auto" : "370px", 
                scrollSnapAlign: "start",
                border: "1px solid #f1f3f5",
                borderRadius: "16px",
                boxShadow: "0 12px 24px rgba(0, 0, 0, 0.03)",
                transition: "all 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
              
              }}
            >
              <div>
                {/* Cabecera del Paciente */}
                <div className="d-flex align-items-center gap-3 mb-3">
                  <img 
                    src={authorPhoto} 
                    alt={authorName} 
                    className="rounded-circle border border-2" 
                    style={{ borderColor: "#f1f3f5", objectFit: "cover" }}
                    width="44" 
                    height="44" 
                    draggable="false" 
                  />
                  <div>
                    <h6 className="mb-0 fw-bold text-dark text-truncate" style={{ fontSize: "14px", maxWidth: "210px", letterSpacing: "-0.2px" }}>
                      {authorName}
                    </h6>
                    <small className="text-muted" style={{ fontSize: "11px" }}>
                      {publishTime}
                    </small>
                  </div>
                </div>

                {/* Estrellas Doradas */}
                <div className="mb-3" style={{ color: "#FFB700", fontSize: "13px", letterSpacing: "1.5px" }}>
                  {"★".repeat(Math.round(currentRating))}
                </div>

                {/* Comentario */}
                <p 
                  className="mb-0" 
                  style={{ 
                    fontSize: "13.5px", 
                    lineHeight: "1.5", 
                    color: "#4A5568",
                    overflow: "hidden",
                    display: isExpanded ? "block" : "-webkit-box",
                    WebkitLineClamp: isExpanded ? "unset" : 4, 
                    WebkitBoxOrient: "vertical"
                  }}
                >
                  {reviewText}
                </p>
              </div>

              {/* Botón de expansión elegante */}
              {isLongText && (
                <div 
                  onClick={(e) => toggleExpand(index, e)}
                  className="text-primary fw-semibold mt-3 text-end d-inline-block ms-auto review-expand-btn" 
                  style={{ fontSize: "11.5px", cursor: "pointer", letterSpacing: "0.2px" }}
                >
                  {isExpanded ? "Ver menos ↑" : "Leer reseña completa ↓"}
                </div>
              )}
            </div>
          );
        })}
      </div>
          {/* Flecha Derecha - Oculta en Mobile */}
          <button 
          onClick={() => scroll("right")}
          className="btn btn-light rounded-circle d-none d-md-flex align-items-center justify-content-center position-absolute slider-arrow-btn"
          style={{
            right: "-20px",
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 10,
            width: "44px",
            height: "44px",
            boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
            border: "1px solid #e2e8f0",
            backgroundColor: "#ffffff",
            transition: "all 0.2s ease"
          }}
          aria-label="Siguiente"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="#4A5568" viewBox="0 0 16 16">
            <path fillRule="evenodd" d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z"/>
          </svg>
        </button>
      </div>
      {/* Estilos CSS Embebidos Modificados */}
      <style>{`
        /* Oculta la barra de desplazamiento en todos los navegadores */
        .hide-scrollbar::-webkit-scrollbar {
          display: none; 
        }
        .hide-scrollbar {
          -ms-overflow-style: none;  
          scrollbar-width: none;  
        }

        /* Estados del cursor para indicar que es arrastrable */
        .is-grab {
          cursor: grab;
        }
        .is-grabbing {
          cursor: grabbing;
        }

        /* Efecto elegante al pasar el mouse por arriba de cada reseña */
        .review-card:hover {
          transform: translateY(-6px);
          box-shadow: 0 20px 35px rgba(0, 0, 0, 0.06) !important;
          border-color: #e2e8f0 !important;
        }

        /* Efecto hover sutil en el botón de leer más */
        .review-expand-btn:hover {
          text-decoration: underline;
          color: #0d6efd !important;
        }
          /* Hover interactivo premium en las flechas navegadoras */
        .slider-arrow-btn:hover {
          background-color: #197bff !important;
          border-color: #197bff !important;
          transform: translateY(-50%) scale(1.08) !important;
        }
        .slider-arrow-btn:hover svg {
          fill: #ffffff !important;
        }
      `}</style>
    </div>
  );
};

export default GoogleReviewsDirect;